import type {
  ExpenseCategory,
  InvestmentProperty,
  Portfolio,
  Scenario
} from "../domain/models";
import { builtInScenarios } from "../scenarios/scenarioEngine";

export const CURRENT_SCHEMA_VERSION = 2;

export interface AppSettings {
  theme: "light" | "dark";
  onboardingCompleted: boolean;
  proEnabled: boolean;
  planTier: "FREE" | "PRO";
  proTrialEndsAt: string | null;
  proTrialUsed: boolean;
}

export interface PersistedState {
  schemaVersion: number;
  portfolio: Portfolio;
  scenarios: Scenario[];
  settings: AppSettings;
}

const nowIso = () => new Date().toISOString();

const defaultExpenseCategories = (): ExpenseCategory[] => [
  {
    id: "cat-agent",
    name: "Agent Fees",
    items: [
      {
        id: "item-letting",
        name: "Letting fee",
        amount: 550,
        cadence: "ANNUAL",
        growthRate: 2.5
      },
      {
        id: "item-lease-renewal",
        name: "Lease renewal fee",
        amount: 280,
        cadence: "ANNUAL",
        growthRate: 2.5
      }
    ]
  },
  {
    id: "cat-repairs",
    name: "Repairs & Maintenance",
    items: [
      {
        id: "item-routine-repairs",
        name: "Routine repairs",
        amount: 120,
        cadence: "MONTHLY",
        growthRate: 2.5
      },
      {
        id: "item-major-repair",
        name: "Major repair reserve",
        amount: 7000,
        cadence: "ONE_OFF",
        appliesInYears: [4, 9, 14],
        growthRate: 3
      }
    ]
  }
];

const normalizeProperty = (
  property: InvestmentProperty
): InvestmentProperty => ({
  ...property,
  expenses: {
    ...property.expenses,
    agentFeesAnnual: property.expenses.agentFeesAnnual ?? 0,
    categories: property.expenses.categories ?? defaultExpenseCategories()
  }
});

export const defaultPortfolio = (): Portfolio => ({
  id: "portfolio-default",
  name: "My Portfolio",
  assumptions: {
    forecastYears: 20,
    defaultCapitalGrowthRate: 5,
    defaultRentGrowthRate: 3,
    defaultExpenseInflationRate: 2.5,
    defaultInflationRate: 2.5
  },
  properties: [],
  createdAt: nowIso(),
  updatedAt: nowIso()
});

export const defaultPersistedState = (): PersistedState => ({
  schemaVersion: CURRENT_SCHEMA_VERSION,
  portfolio: defaultPortfolio(),
  scenarios: builtInScenarios(),
  settings: {
    theme: "dark",
    onboardingCompleted: false,
    proEnabled: false,
    planTier: "FREE",
    proTrialEndsAt: null,
    proTrialUsed: false
  }
});

export const migrateState = (input: unknown): PersistedState => {
  if (!input || typeof input !== "object") {
    return defaultPersistedState();
  }

  const partial = input as Partial<PersistedState>;
  const basePortfolio = partial.portfolio ?? defaultPortfolio();

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    portfolio: {
      ...basePortfolio,
      properties: basePortfolio.properties.map(normalizeProperty)
    },
    scenarios: partial.scenarios ?? builtInScenarios(),
    settings: {
      theme: partial.settings?.theme ?? "dark",
      onboardingCompleted: partial.settings?.onboardingCompleted ?? false,
      proEnabled: partial.settings?.proEnabled ?? false,
      planTier: partial.settings?.planTier ?? "FREE",
      proTrialEndsAt: partial.settings?.proTrialEndsAt ?? null,
      proTrialUsed: partial.settings?.proTrialUsed ?? false
    }
  };
};

export const createDefaultExpenseCategories = defaultExpenseCategories;
